import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../components/LanguageSelector';
import TicketDetailModal from '../components/TicketDetailModal';
import TestSuiteSwimlanes from '../components/TestSuiteSwimlanes';
import Downloads from '../components/Downloads';
import Certifications from '../components/Certifications';
import FAQ from '../components/FAQ';
import NotificationBell from '../components/NotificationBell';
import HelpModal from '../components/HelpModal';
import PageLoader from '../components/PageLoader';
import { authStorage } from '../services/authStorage';
import { authApi, ApiError } from '../services/authApi';
import { Company, TestSuite, TestDetails, Receipt, Certification, CertificationResponse } from '../types/auth';
import { mapStatusToAPI, formatStatusForDisplay } from '../utils/statusUtils';
import { processOptionalTest } from '../utils/optionalTestUtils';
import { getAvatarUrl } from '../utils/avatarUtils';
import '../styles/TestSuitePage.css';

interface TestTicket {
  id: number;
  ticketNumber: string;
  status: 'todo' | 'inProgress' | 'passed' | 'failed';
  description?: string;
  date: string;
  vatRate: string;
  requiresUpload?: boolean;
  assignee?: {
    name: string;
    avatar: string;
  };
  testNumber?: number;
  receipt?: Receipt | null;
  testSuiteRuleMappingId?: number;
  testName?: string;
  testRuleInfoId?: number; // Add this to track the test rule info ID for uploads
}

// Add the props interface
interface TestSuitePageProps {
  onLogout?: () => void;
  currentUser?: {
    username: string;
    role: 'user' | 'admin';
  };
}

const TestSuitePage: React.FC<TestSuitePageProps> = ({ onLogout, currentUser }) => {
  const { t } = useTranslation();
  const [showScrollHint, setShowScrollHint] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<TestTicket | null>(null);
  const [ticketComments, setTicketComments] = useState<any[]>([]);
  
  // Company and test suite management state
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [testSuites, setTestSuites] = useState<TestSuite[]>([]);
  const [selectedTestSuite, setSelectedTestSuite] = useState<TestSuite | null>(null);
  const [testDetails, setTestDetails] = useState<TestDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isProcessingOptionalTest, setIsProcessingOptionalTest] = useState(false);
  const [notificationRefreshTrigger, setNotificationRefreshTrigger] = useState(false);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [showCreateTestSuiteButton, setShowCreateTestSuiteButton] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const loadingTestSuitesRef = useRef(false);
  const loadingTestDetailsRef = useRef(false);
  const hasLoadedTestSuitesRef = useRef<Set<number>>(new Set());
  
  // Check if viewport is mobile sized
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1200);
    };
    
    // Initial check
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize component
  useEffect(() => {
    initializeComponent();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load test suites when company is selected (only once per company)
  useEffect(() => {
    if (selectedCompanyId && !loadingTestSuitesRef.current && !hasLoadedTestSuitesRef.current.has(selectedCompanyId)) {
      loadTestSuites(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  // Load test details when test suite is selected (only if we don't have details yet)
  useEffect(() => {
    if (selectedTestSuite && !testDetails && !loadingTestDetailsRef.current) {
      loadTestDetails(selectedTestSuite.id);
    }
  }, [selectedTestSuite, testDetails]);

  // Convert test details to tickets for company users
  useEffect(() => {
    if (!isAdmin && testDetails && testDetails.testRules) {
      const convertedTickets: TestTicket[] = testDetails.testRules.map(rule => {
        const vatMatch = rule.testName.match(/(\d+%)/g);
        const vatRate = vatMatch ? vatMatch.join(', ') : rule.testName;
        
        // Determine status based on receipt and its last status
        let status: 'todo' | 'inProgress' | 'passed' | 'failed' = 'todo';
        let assigneeInfo = undefined;
        let ticketNumber = 'Upload Test';
        
        if (rule.receipt) {
          ticketNumber = 'Test Uploaded';
          assigneeInfo = {
            name: rule.receipt.uploadedByUser.username,
            avatar: getAvatarUrl(rule.receipt.uploadedByUser.profilePicUrl, rule.receipt.uploadedByUser.username)
          };
          
          // If there's a testedByUser, use that as the assignee instead
          if (rule.receipt.testedByUser) {
            assigneeInfo = {
              name: rule.receipt.testedByUser.username,
              avatar: getAvatarUrl(rule.receipt.testedByUser.profileImageUrl, rule.receipt.testedByUser.username)
            };
          }
          
          // Map receipt status to ticket status
          switch (rule.receipt.lastStatus) {
            case 'To_Do':
              status = 'todo';
              break;
            case 'In_Progress':
              status = 'inProgress';
              break;
            case 'Passed':
              status = 'passed';
              break;
            case 'Failed':
              status = 'failed';
              break;
            default:
              // Check if we have events to determine current status
              if (rule.receipt.events && rule.receipt.events.length > 0) {
                const lastEvent = rule.receipt.events[rule.receipt.events.length - 1];
                switch (lastEvent.status) {
                  case 'To_Do':
                    status = 'todo';
                    break;
                  case 'In_Progress':
                    status = 'inProgress';
                    break;
                  case 'Passed':
                    status = 'passed';
                    break;
                  case 'Failed':
                    status = 'failed';
                    break;
                  default:
                    status = 'todo';
                }
              } else {
                status = 'todo';
              }
          }
        }
        
        return {
          id: rule.testRuleInfoId, // Use testRuleInfoId instead of testNumber for unique identification
          ticketNumber,
          status,
          date: rule.receipt ? rule.receipt.uploadedTimestamp.split(' ')[0] : testDetails.lastUpdatedUser.lastUpdatedDate.split(' ')[0],
          vatRate: vatRate,
          requiresUpload: !rule.receipt || rule.receipt.lastStatus === 'Failed' || (rule.receipt.events && rule.receipt.events.length > 0 && rule.receipt.events[rule.receipt.events.length - 1].status === 'Failed'),
          assignee: assigneeInfo,
          description: rule.testDescription,
          testNumber: rule.testNumber, // Keep test number for display
          receipt: rule.receipt,
          testSuiteRuleMappingId: rule.receipt ? rule.receipt.testSuiteRuleMappingId : rule.testRuleInfoId,
          testName: rule.testName
        };
      });
      
      setTestTickets(convertedTickets);
    } else if (!isAdmin) {
      setTestTickets([]);
    }
  }, [isAdmin, testDetails]);

  const initializeComponent = async () => {
    try {
      setLoading(true);
      setError('');
      
      const userData = authStorage.getUserData();
      if (!userData) {
        setError('User data not found. Please log in again.');
        return;
      }

      // Check if user is admin (companyId is null)
      const adminUser = userData.companyId === null;
      setIsAdmin(adminUser);

      // Auto-show help modal for all non-admin users on every login
      if (!adminUser) {
        // Delay showing help modal to let the page load first
        setTimeout(() => {
          setShowHelpModal(true);
        }, 1500);
      }

      if (adminUser) {
        // Admin user - load all companies
        await loadCompanies();
      } else {
        // Company user - load test suites for their company
        setSelectedCompanyId(userData.companyId);
        
        // Get company data from encrypted storage
        const companyData = authStorage.getCompanyData();
        const company = companyData 
          ? { id: userData.companyId, companyName: companyData.companyName, orgNumber: companyData.orgNumber } as Company
          : { id: userData.companyId, companyName: 'My Company' } as Company;
        setSelectedCompany(company);
        // Note: loadTestSuites will be called by the useEffect when selectedCompanyId changes
      }
    } catch (error) {
      console.error('Failed to initialize component:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to initialize. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const companiesData = await authApi.getAllCompanies(token);
      setCompanies(companiesData);
    } catch (error) {
      console.error('Failed to load companies:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to load companies. Please try again.');
      }
    }
  };

  const loadTestSuites = async (companyId: number) => {
    try {
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      // Prevent calling API if already loading or already loaded for this company
      if (loadingTestSuitesRef.current || hasLoadedTestSuitesRef.current.has(companyId)) {
        return;
      }

      loadingTestSuitesRef.current = true;
      hasLoadedTestSuitesRef.current.add(companyId);
      
      const testSuiteData = await authApi.getTestSuitesByCompany(token, companyId);
      
      // Always show test suites where isHistory = false first
      const activeTestSuites = testSuiteData.testSuites.filter(ts => !ts.isHistory);
      
      if (activeTestSuites.length === 0) {
        // If response has "testSuites":[] or all test suites have isHistory = true,
        // then show the last test suite and create button for admins
        const historicalTestSuites = testSuiteData.testSuites.filter(ts => ts.isHistory);
        
        if (historicalTestSuites.length > 0) {
          // Show the last historical test suite
          const lastHistoricalSuite = historicalTestSuites[historicalTestSuites.length - 1];
          setTestSuites([lastHistoricalSuite]);
          setSelectedTestSuite(lastHistoricalSuite);
          setError(''); // Clear any previous error since we have a historical suite
          setShowCreateTestSuiteButton(isAdmin); // Show create button for admins only
        } else {
          // No test suites found at all - show message
          setTestSuites([]);
          setSelectedTestSuite(null);
          setTestDetails(null);
          setError('Test suite is not yet created for this company.');
          setShowCreateTestSuiteButton(isAdmin); // Show create button for admins only
        }
      } else {
        // Show active test suites (should typically be only one active at a time)
        setTestSuites(activeTestSuites);
        setSelectedTestSuite(activeTestSuites[0]);
        setError(''); // Clear any previous error
        setShowCreateTestSuiteButton(false); // Don't show create button when active suite exists
      }
    } catch (error) {
      console.error('Failed to load test suites:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to load test suites. Please try again.');
      }
      // Clear existing data on error
      setTestSuites([]);
      setSelectedTestSuite(null);
      setTestDetails(null);
      // Remove from loaded set on error so it can be retried
      hasLoadedTestSuitesRef.current.delete(companyId);
    } finally {
      loadingTestSuitesRef.current = false;
    }
  };

  const loadTestDetails = async (testSuiteId: number) => {
    try {
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      // Prevent calling API if already loading
      if (loadingTestDetailsRef.current) {
        return;
      }

      loadingTestDetailsRef.current = true;
      const details = await authApi.getTestDetails(token, testSuiteId);
      setTestDetails(details);
    } catch (error) {
      console.error('Failed to load test details:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to load test details. Please try again.');
      }
    } finally {
      loadingTestDetailsRef.current = false;
    }
  };

  const handleCompanySelect = (companyId: number) => {
    const company = companies.find(c => c.id === companyId);
    setSelectedCompanyId(companyId);
    setSelectedCompany(company || null);
    
    // Clear existing data when switching companies
    setTestSuites([]);
    setSelectedTestSuite(null);
    setTestDetails(null);
    setError('');
    
    // Reset loading states
    loadingTestSuitesRef.current = false;
    loadingTestDetailsRef.current = false;
  };

  const handleCreateTestSuite = async () => {
    if (!selectedCompanyId) return;

    try {
      setError('');
      const token = authStorage.getAccessToken();
      const userData = authStorage.getUserData();
      
      if (!token || !userData) {
        setError('Authentication data not found. Please log in again.');
        return;
      }

      await authApi.createTestSuite(token, {
        companyId: selectedCompanyId,
        lastUpdatedBy: userData.id
      });

      // Clear the loaded tracking for this company and reload test suites
      hasLoadedTestSuitesRef.current.delete(selectedCompanyId);
      await loadTestSuites(selectedCompanyId);
    } catch (error) {
      console.error('Failed to create test suite:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to create test suite. Please try again.');
      }
    }
  };
  
  // Hide scroll hint after user has scrolled horizontally
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollX > 10) {
        setShowScrollHint(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Auto-hide after 5 seconds
    const timer = setTimeout(() => {
      setShowScrollHint(false);
    }, 5000);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);
  
  // State for managing tickets (generated from test details for company users)
  const [testTickets, setTestTickets] = useState<TestTicket[]>([]);
  
  // State for managing tickets (only for admin users - using mock data)
  const [tickets, setTickets] = useState<TestTicket[]>(() => {
    const initialTickets: TestTicket[] = [];
    
    for (let i = 1; i <= 10; i++) {
      const statuses: Array<'todo' | 'inProgress' | 'passed' | 'failed'> = ['todo', 'todo', 'todo', 'todo', 'inProgress', 'inProgress', 'passed', 'passed', 'failed', 'failed'];
      const status = statuses[i - 1];
      
      const vatRates = [
        t('testSuite.normalSaleWith25'),
        t('testSuite.normalSaleWith12'),
        t('testSuite.normalSaleWith6'),
        t('testSuite.normalSaleWithMultiple')
      ];
      
      const assignees = [
        { name: 'Anna Johansson', avatar: getAvatarUrl(null, 'Anna Johansson') },
        { name: 'Erik Larsson', avatar: getAvatarUrl(null, 'Erik Larsson') },
        { name: 'Maria Nilsson', avatar: getAvatarUrl(null, 'Maria Nilsson') },
        { name: 'Johan Andersson', avatar: getAvatarUrl(null, 'Johan Andersson') }
      ];
      
      initialTickets.push({
        id: i, // Use simple numbering 1-10
        ticketNumber: i <= 2 || i === 9 ? 'Upload Test' : `Tickster-2025-04-01-${String(i).padStart(3, '0')}`,
        status,
        date: '2025-09-24',
        vatRate: vatRates[i % vatRates.length],
        requiresUpload: i <= 2 || i === 9,
        assignee: status !== 'todo' ? assignees[i % assignees.length] : undefined,
        description: status === 'failed' ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec id fermentum eros, rhoncus pellentesque lactusat amet. tristique condimentum, hulla scelerisque est id dui ornare faucibus. Ut imperdiet du...' : undefined
      });
    }
    
    return initialTickets;
  });

  // Function to handle ticket click
  const handleTicketClick = (ticket: TestTicket) => {
    // For company users: allow clicking on cards without receipts to upload receipts
    // For admin users: allow clicking on any card (with or without receipts) to upload receipts or view details
    if (!isAdmin && !ticket.receipt) {
      // Company user clicking on a card without receipt - allow them to open modal to upload
      setSelectedTicket(ticket);
      setTicketComments([]); // No comments yet
      return;
    }
    
    // Admin users can now click on any ticket (with or without receipts)
    // Cards with receipts - show existing behavior with comments
    if (!ticket.receipt) {
      // Admin user clicking on card without receipt - allow them to open modal to upload
      setSelectedTicket(ticket);
      setTicketComments([]); // No comments yet
      return;
    }
    
    setSelectedTicket(ticket);
    
    // Generate comments from receipt events
    const comments: any[] = [];
    let commentId = 1;
    
    if (ticket.receipt && ticket.receipt.events) {
      ticket.receipt.events.forEach(event => {
        // Map event names to user-friendly text
        let eventText = event.comment || 'Updated status';
        if (event.eventName === 'Uploaded_Receipt') {
          eventText = `Uploaded receipt for ${ticket.vatRate}`;
        } else if (event.eventName === 'Updated_Status') {
          eventText = event.comment || `Status updated to ${event.status.replace('_', ' ')}`;
        } else if (event.eventName === 'Added_Comment') {
          eventText = event.comment || 'Added a comment';
        }

        comments.push({
          id: commentId++,
          author: {
            name: event.commentedByUser?.username || ticket.receipt!.uploadedByUser.username,
            avatar: event.commentedByUser?.profileImageUrl 
              ? getAvatarUrl(event.commentedByUser.profileImageUrl, event.commentedByUser.username)
              : getAvatarUrl(ticket.receipt!.uploadedByUser.profilePicUrl, ticket.receipt!.uploadedByUser.username)
          },
          date: event.commentTimestamp?.split(' ')[0] || ticket.receipt!.uploadedTimestamp.split(' ')[0],
          text: eventText,
          status: event.eventName === 'Updated_Status' ? event.status : undefined
        });
        
        // Add child events if they exist
        if (event.childEvents && event.childEvents.length > 0) {
          event.childEvents.forEach(childEvent => {
            if (childEvent.comment && childEvent.commentedByUser) {
              comments.push({
                id: commentId++,
                author: {
                  name: childEvent.commentedByUser.username,
                  avatar: getAvatarUrl(childEvent.commentedByUser.profileImageUrl, childEvent.commentedByUser.username)
                },
                date: childEvent.commentTimestamp?.split(' ')[0] || ticket.date,
                text: childEvent.comment,
                status: childEvent.eventName === 'Updated_Status' ? childEvent.status : undefined
              });
            }
          });
        }
      });
    }
    
    setTicketComments(comments);
  };

  // Function to handle test rule click (convert test rule to ticket format)
  const handleTestRuleClick = (rule: any) => {
    // Convert test rule to ticket format for modal
    const testRuleAsTicket: TestTicket = {
      id: rule.testNumber,
      ticketNumber: rule.receipt ? 'Test Uploaded' : 'Upload Test',
      status: rule.receipt ? 'inProgress' : 'todo', // Simple status based on receipt
      description: rule.testDescription,
      date: new Date().toISOString().split('T')[0], // Today's date as placeholder
      vatRate: rule.testName,
      requiresUpload: !rule.receipt,
      testSuiteRuleMappingId: rule.receipt ? rule.receipt.testSuiteRuleMappingId : rule.testRuleInfoId,
      testNumber: rule.testNumber,
      receipt: rule.receipt,
      testName: rule.testName
    };

    // Use the same logic as handleTicketClick
    handleTicketClick(testRuleAsTicket);
  };

  // Handle ticket drop with API call
  const handleTicketDrop = async (ticketId: number, newStatus: 'todo' | 'inProgress' | 'passed' | 'failed') => {
    try {
      // Only allow admin users to change status via drag and drop
      if (!isAdmin) {
        setError('You do not have permission to change status. Company users can only upload receipts.');
        return;
      }

      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      // Find the ticket to get its testSuiteRuleMappingId
      const ticket = testTickets.find(t => t.id === ticketId);
      if (!ticket) {
        console.error('Ticket not found:', ticketId);
        return;
      }

      const mappingId = ticket.testSuiteRuleMappingId || ticket.testNumber || ticket.id;
      
      // Call updateTest API
      await authApi.updateTest(token, {
        testSuiteRuleMappingId: mappingId,
        status: mapStatusToAPI(newStatus),
        comment: `Status updated to ${formatStatusForDisplay(newStatus)}`,
      });

      // Update local state only after successful API call
      setTestTickets(prevTickets => 
        prevTickets.map(t => 
          t.id === ticketId 
            ? { ...t, status: newStatus }
            : t
        )
      );

      // Reload test details to get updated data from server
      if (selectedTestSuite) {
        await loadTestDetails(selectedTestSuite.id);
      }

      // Refresh notifications for company users to see status changes
      setNotificationRefreshTrigger(prev => !prev);
    } catch (error) {
      console.error('Failed to update test status:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to update test status. Please try again.');
      }
    }
  };

  const handleMarkAsOptional = async (ticket: TestTicket) => {
    if (isProcessingOptionalTest) return; // Prevent multiple simultaneous processing

    try {
      setIsProcessingOptionalTest(true);
      setError(''); // Clear any previous errors
      
      await processOptionalTest(ticket);
      
      // Refresh the test details
      if (selectedTestSuite) {
        await loadTestDetails(selectedTestSuite.id);
      }
      
      // Refresh notifications
      setNotificationRefreshTrigger(prev => !prev);
    } catch (error) {
      console.error('Failed to mark test as optional:', error);
      setError(error instanceof Error ? error.message : 'Failed to mark test as optional');
    } finally {
      setIsProcessingOptionalTest(false);
    }
  };

  // Add state and functions for the new features
  const [activeFeature, setActiveFeature] = useState<'testsuite' | 'downloads' | 'certificates' | 'faq'>('testsuite');

  const handleDownloadsClick = () => {
    setActiveFeature('downloads');
    console.log('Downloads section clicked');
    // You can implement the downloads functionality here
  };

  const handleCertificatesClick = () => {
    setActiveFeature('certificates');
    console.log('Certificates section clicked');
    // You can implement the certificates functionality here
  };

  return (
    <div className="test-suite-page">
      <header className="test-suite-header">
        <div className="logo-container">
          <img src="https://infrasec.se/wp-content/uploads/2024/09/Infrasec_lockup_01@4x.png" alt="Infrasec" className="logo" />
        </div>
          <div className="header-controls">
         {/* <LanguageSelector /> */}
          {!isAdmin && (
            <>
              <button 
                className="help-button" 
                onClick={() => setShowHelpModal(true)}
                title="Help - How to use the system"
              >
                <span className="help-icon">?</span>
              </button>
              <NotificationBell 
                refreshTrigger={notificationRefreshTrigger}
                isAdmin={false}
              />
            </>
          )}
          {currentUser && (
            <div className="user-info">
              <div className="avatar">
                <img src={getAvatarUrl(null, currentUser.username)} alt={currentUser.username} />
              </div>
              <span className="user-name">
                {currentUser.username}
              </span>
            </div>
          )}
          </div>
      </header>

      <main className="test-suite-content">
        <h1>
          {activeFeature === 'testsuite' && t('testSuite.title')}
          {activeFeature === 'downloads' && t('downloads.title')}
          {activeFeature === 'certificates' && 'Certificates'}
          {activeFeature === 'faq' && 'FAQ'}
        </h1>
        
        {/* Display company name for non-admin users */}
        {!isAdmin && selectedCompany && (
          <div className="company-name-header">
            <h2>{selectedCompany.companyName}</h2>
          </div>
        )}
        
        {isMobile && (
          <div className="scroll-indicator">
            <span className="arrow">←</span>
            {t('testSuite.scrollHint')}
            <span className="arrow">→</span>
          </div>
        )}
        
        <div className="test-suite-swimlanes">
          <div className="sidebar">
            <div className="sidebar-menu">
              <button 
                className={`sidebar-button ${activeFeature === 'testsuite' ? 'active' : ''}`}
                onClick={() => setActiveFeature('testsuite')}
              >
                <span className="icon document-icon"></span>
                <span className="tooltip">{t('sidebar.testSuite')}</span>
              </button>
              <button 
                className={`sidebar-button ${activeFeature === 'downloads' ? 'active' : ''}`}
                onClick={handleDownloadsClick}
              >
                <span className="icon download-icon"></span>
                <span className="tooltip">{t('sidebar.downloads')}</span>
              </button>
              <button 
                className={`sidebar-button ${activeFeature === 'certificates' ? 'active' : ''}`}
                onClick={handleCertificatesClick}
              >
                <span className="icon certificate-icon"></span>
                <span className="tooltip">{t('sidebar.certificates')}</span>
              </button>
              <button
                className={`sidebar-button ${activeFeature === 'faq' ? 'active' : ''}`}
                onClick={() => setActiveFeature('faq')}
              >
                <span className="icon faq-icon"></span>
                <span className="tooltip">FAQ</span>
              </button>
              {onLogout && (
                <button className="sidebar-button logout-sidebar-button" onClick={onLogout}>
                  <span className="icon logout-icon"></span>
                  <span className="tooltip">{t('sidebar.logout')}</span>
                </button>
              )}
            </div>
          </div>
          
          {/* Show TestSuiteSwimlanes when testsuite is active */}
          {activeFeature === 'testsuite' && (
            <div className="main-swimlanes-content">
              {/* Admin Test Suite Management Interface */}
              {isAdmin && (
                <div className="admin-test-suite-section">
                  <div className="company-selection-sidebar">
                    <div className="sidebar-header">
                      <h3>{t('testSuite.activeCompanies', 'Active Companies')}</h3>
                      <span className="company-count">
                        {companies.filter(company => company.isActive).length} {t('testSuite.companies', 'companies')}
                      </span>
                    </div>
                    {loading ? (
                      <div className="loading-companies">
                        <div className="loading-spinner"></div>
                        <p>{t('common.loading', 'Loading companies...')}</p>
                      </div>
                    ) : companies.length > 0 ? (
                      <div className="company-list">
                        {companies.filter(company => company.isActive).map((company) => (
                          <div 
                            key={company.id} 
                            className={`company-item ${selectedCompanyId === company.id ? 'selected' : ''}`}
                            onClick={() => handleCompanySelect(company.id)}
                          >
                            <div className="company-avatar-small">
                              <img 
                                src={`https://via.placeholder.com/48/4F46E5/FFFFFF/?text=${company.companyName.charAt(0).toUpperCase()}`}
                                alt={company.companyName}
                              />
                            </div>
                            <div className="company-info">
                              <h4>{company.companyName}</h4>
                              <p className="company-org">{company.orgNumber}</p>
                              <div className="company-users">
                                {company.users && company.users.length > 0 && (
                                  <span className="user-count">
                                    {company.users.length} {company.users.length === 1 ? t('common.user', 'user') : t('common.users', 'users')}
                                  </span>
                                )}
                              </div>
                            </div>
                            {selectedCompanyId === company.id && (
                              <div className="selection-indicator">
                                <span>→</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-companies">
                        <div className="empty-state-small">
                          <span className="empty-icon">🏢</span>
                          <p>{t('testSuite.noCompanies', 'No active companies found.')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="test-suite-main">
                    {selectedCompany ? (
                      <div className="selected-company-section">
                        <div className="company-header">
                          <h2>{selectedCompany.companyName}</h2>
                          <p>{t('testSuite.orgNumber', 'Organization Number')}: {selectedCompany.orgNumber}</p>
                        </div>
                        
                        <div className="test-suite-header-with-action">
                          <div className="test-suite-section-header">
                            <h3>{t('testSuite.activeSuite', 'Active Test Suite')}</h3>
                          </div>
                          {(testSuites.length === 0 || showCreateTestSuiteButton) && (
                            <button 
                              className="create-test-suite-btn primary-action"
                              onClick={handleCreateTestSuite}
                            >
                              {t('testSuite.createTestSuite', 'Create Test Suite')}
                            </button>
                          )}
                        </div>

                        {testSuites.length === 0 ? (
                          <div className="no-test-suite">
                            <div className="empty-state">
                              <div className="empty-state-icon">📋</div>
                              <h3>{t('testSuite.noTestSuite', 'No Test Suite Found')}</h3>
                              <p>{t('testSuite.noTestSuiteDesc', 'This company does not have an active test suite yet. Create one to get started.')}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="test-suite-details">
                            {selectedTestSuite && testDetails && (
                              <>
                                <div className="test-suite-info">
                                  <div className="test-suite-meta-card">
                                    <div className="test-suite-status">
                                      <span className={`status-badge status-${selectedTestSuite.status.toLowerCase().replace('_', '-')}`}>
                                        {selectedTestSuite.status.replace('_', ' ')}
                                      </span>
                                      {selectedTestSuite.isHistory && (
                                        <span className="history-badge">
                                          {t('testSuite.historicalSuite', 'Historical')}
                                        </span>
                                      )}
                                      <h4>
                                        {t('testSuite.testSuiteId', 'Test Suite')} #{selectedTestSuite.id}
                                        {selectedTestSuite.isHistory && showCreateTestSuiteButton && (
                                          <small className="create-new-hint">
                                            {t('testSuite.createNewHint', ' - Create a new one above')}
                                          </small>
                                        )}
                                      </h4>
                                    </div>
                                    <div className="test-suite-meta">
                                      <div className="meta-item">
                                        <span className="meta-label">{t('testSuite.lastUpdatedBy', 'Last Updated By')}:</span>
                                        <div className="meta-user">
                                          <img 
                                            src={getAvatarUrl(testDetails.lastUpdatedUser.profilePicUrl, testDetails.lastUpdatedUser.username)}
                                            alt={testDetails.lastUpdatedUser.username}
                                            className="user-avatar-small"
                                          />
                                          <span>{testDetails.lastUpdatedUser.username}</span>
                                        </div>
                                      </div>
                                      <div className="meta-item">
                                        <span className="meta-label">{t('testSuite.lastUpdatedDate', 'Last Updated')}:</span>
                                        <span>{testDetails.lastUpdatedUser.lastUpdatedDate}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="test-rules-section">
                                  <div className="test-rules-header">
                                    <h4>{t('testSuite.testRules', 'Test Rules')} ({testDetails.testRules.length})</h4>
                                    <p>{t('testSuite.testRulesDesc', 'Complete all test cases to finish the test suite')}</p>
                                  </div>
                                  <div className="test-rules-grid">
                                    {testDetails.testRules.map((rule) => (
                                      <div key={rule.testNumber} className="test-rule-card" onClick={() => handleTestRuleClick(rule)}>
                                        <div className="test-rule-header">
                                          <div className="test-number-badge">
                                            <span className="test-number">{rule.testNumber}</span>
                                          </div>
                                        </div>
                                        <div className="test-rule-content">
                                          <h5 className="test-rule-name">{rule.testName}</h5>
                                        </div>
                                        <div className="test-rule-footer">
                                          {/* Show upload button only when receipt is null */}
                                          {!rule.receipt && (
                                            <button className="upload-receipt-btn" title={t('testSuite.uploadReceipt', 'Upload Test') || 'Upload Test'}>
                                              <span className="upload-icon">📄</span>
                                            </button>
                                          )}
                                          {/* Show download button only when receipt exists */}
                                          {rule.receipt && (
                                            <button className="download-receipt-btn" title={t('testSuite.downloadReceipt', 'Download Test') || 'Download Test'}>
                                              <span className="download-icon">⬇️</span>
                                            </button>
                                          )}
                                          <div className="test-status-indicator">
                                            {rule.receipt ? (
                                              <span className="status-icon completed">✓</span>
                                            ) : (
                                              <span className="status-icon pending">⏱</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        
                        {error && (
                          <div className="error-message">
                            {error}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="no-company-selected">
                        <div className="empty-state">
                          <h3>{t('testSuite.selectCompanyPrompt', 'Select a Company')}</h3>
                          <p>{t('testSuite.selectCompanyDesc', 'Please select a company from the left sidebar to view or create test suites.')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Company User Test Suite Interface */}
              {!isAdmin && (
                <div className="company-user-interface">
                  {loading ? (
                    <div className="loading-companies">
                      <div className="loading-spinner"></div>
                      <p>{t('common.loading', 'Loading test suite...')}</p>
                    </div>
                  ) : error ? (
                    <div className="error-state">
                      <div className="error-icon">⚠️</div>
                      <h3>{t('testSuite.error', 'Error')}</h3>
                      <p>{error}</p>
                      <button onClick={() => {
                        setError('');
                        if (selectedCompanyId) {
                          loadTestSuites(selectedCompanyId);
                        }
                      }} className="retry-button">
                        {t('common.retry', 'Try Again')}
                      </button>
                    </div>
                  ) : testTickets.length > 0 ? (
                    <TestSuiteSwimlanes 
                      tickets={testTickets}
                      onTicketClick={handleTicketClick}
                      onTicketDrop={handleTicketDrop}
                      showCounts={true}
                      isAdmin={isAdmin}
                      onMarkAsOptional={handleMarkAsOptional}
                    />
                  ) : (
                    <div className="no-test-suite">
                      <div className="empty-state">
                        <div className="empty-state-icon">📋</div>
                        <h3>{t('testSuite.noTestSuite', 'No Test Suite Found')}</h3>
                        <p>{t('testSuite.noTestSuiteCompanyDesc', 'Your company does not have an active test suite yet. Please contact your administrator.')}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}        {/* Show Downloads when downloads is active */}
        {activeFeature === 'downloads' && (
          <div className="main-swimlanes-content">
            <Downloads isAdmin={false} currentUser={currentUser} showTitle={false} />
          </div>
        )}
        
        {/* Show Certificates when certificates is active - always available for all users */}
        {activeFeature === 'certificates' && (
          <div className="main-swimlanes-content">
            <Certifications 
              isAdmin={isAdmin} 
              currentUser={currentUser} 
              showTitle={false} 
              companyId={selectedCompanyId || undefined}
            />
          </div>
        )}

        {/* FAQ section - available for all users */}
        {activeFeature === 'faq' && (
          <div className="main-swimlanes-content">
            <FAQ isAdmin={isAdmin} />
          </div>
        )}
        </div>

        {isMobile && showScrollHint && (
          <div className="mobile-scroll-hint">
            <span className="mobile-scroll-hint-icon">↔️</span>
            {t('testSuite.swipeToScroll')}
          </div>
        )}
      </main>

      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          comments={ticketComments}
          onClose={() => setSelectedTicket(null)}
          onMarkAsOptional={handleMarkAsOptional}
          testSuiteId={selectedTestSuite?.id}
          onTestUpdate={() => {
            // Refresh the test details after receipt upload or test update
            if (selectedTestSuite) {
              loadTestDetails(selectedTestSuite.id);
            }
            setSelectedTicket(null);
            // Refresh notifications to show updated status
            setNotificationRefreshTrigger(prev => !prev);
          }}
        />
      )}

      {/* Page Loader for Optional Test Processing */}
      <PageLoader 
        isLoading={isProcessingOptionalTest} 
        message={t('testSuite.processingOptionalTest', 'Processing optional test...') || 'Processing optional test...'}
      />

      {/* Help Modal for Non-Admin Users */}
      {!isAdmin && (
        <HelpModal 
          isOpen={showHelpModal}
          onClose={() => {
            setShowHelpModal(false);
          }}
        />
      )}
    </div>
  );
};

export default TestSuitePage;